/**
 * The calendar days strip's bind half: one bind of a strip (the same-revision
 * reuse, the provider-read settle and reveal, the budgeted queued path or the
 * synchronous one), its structure step and the per-day column bind.
 *
 * The strip reaches this module through `bindStrip` and the lifecycle writers
 * below; this module reaches the strip only through `StripBindHost`, whose
 * members are readonly or methods, and knows its identity only as the
 * opaque `StripKey` the strip mints. The bind's own state lives in
 * `StripBindState`, which only this module assigns (the strip reads it).
 */

import type { EventsCacheSlots } from "@tento-lona/sheets/platform";
import { nonnull } from "@tento-core/assert";
import {
  CalendarReadRevision,
  CompletedCalendarRead,
  type CalendarReadTicket,
  type CalendarReadValue,
} from "@tento-lona/sheets-ui/sheet/calendar-read-revision.ts";
import {
  type CalendarSubcolumn,
  type SubcolumnCreateHandler,
} from "@tento-lona/sheets-ui/sheet/calendar-subcolumn.ts";
import { type Option } from "@tento-lona/sheets";
import type { RowLocalId } from "@tento-lona/sheets";
import { $$ } from "@tento-ui/fastdom";
import { LiveData } from "@tento-core/livedata";
import { DateFragment, DateRegion, DateTime, Time, Utc } from "@tento-chrono";
import { GestureInfo } from "@tento-lona/sheets-ui/sheet/gestures/gesture-info.ts";
import { Tracing } from "@tento-core/tracing";
import type { CalendarSheetColumn } from "./calendar-sheet-column.ts";
import { idleCompleteEnabled, type StripUnit, stripUnits } from "./calendar-strip-work.ts";
import { type RoughBox, RoughBoxes, roughLayerEnabled } from "./calendar-rough-layer.ts";
import type { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import { settleStripReads } from "./calendar-days-strip-read.ts";
import { type OcclusionContext, renderOccludedIndicators } from "./calendar-days-strip-occlusion.ts";
import { bindCurrentTime } from "./calendar-days-strip-current-time.ts";
import { FrameMarks } from "@tento-ui/frame-marks";
import type {
  CalendarDaysStrip,
  DaysStripColumn,
  SheetNamedChildren,
} from "./calendar-days-strip.ts";

/**
 * A strip's identity as an opaque key: the work queue's owner, the clock
 * listener's key and the content-lag meter's owner. Each strip mints one
 * (`new StripKey()`) and keys all of those with it; the key is not the strip,
 * so the bind cannot reach the strip through it.
 */
export class StripKey {
  /** The brand: only a minted key carries it, so no other object is a `StripKey`. */
  readonly #stripKey = true;

  /** Whether `value` is a minted strip key. */
  static is(value: object): value is StripKey {
    return #stripKey in value;
  }
}

/** What a bind reaches on its strip. Readonly members and methods only. */
export interface StripBindHost {
  /** The strip's identity (its `StripKey`): the work queue's, the clock listener's and the content-lag meter's key. */
  readonly key: StripKey;
  /** The dependencies `init` set (throws before `init`). */
  deps(): CalendarSheetColumn.Dependencies;
  /** The signals `init` set (throws before `init`). */
  signals(): CalendarSheetColumn.Signals;
  /** The strip's day columns for the current props (cached until `resetColumns`). */
  columns(): Readonly<DaysStripColumn<SheetNamedChildren>[]>;
  /** Drop the strip's date / region / column cache. */
  resetColumns(): void;
  /** How many dates the strip's cache holds (0 when not computed). */
  cachedDateCount(): number;
  /** Create, style and place one day element per column. */
  visitColumns(visit: (info: DaysStripColumn<SheetNamedChildren>) => void): void;
  /** The in-flight existing-item drag ghost, if any. */
  dragGhost(): Option<CalendarDaysStrip.DragGhost>;
  /** Tell the harness's bind observers (if any attached) that a bind begins; returns their settle, or null with none. */
  notifyBind(props: CalendarDaysStrip.Props, generation: number): Option<() => void>;
  /** Whether the strip is in the document. */
  connected(): boolean;
  /**
   * Whether the strip's column is parked in the scroll pool now
   * (`scroll-column-retention`). Absent: never parked.
   */
  pooled?(): boolean;
  /** Set the strip's observational `binding`. */
  setBinding(binding: CalendarDaysStrip["binding"]): void;
  /** Show the strip's content (the reveal's opacity write). */
  showContent(): void;
  /** Tell the strip's host the presentation ended. */
  endPresentation(): void;
  /** Write the window offset (hours) the day styles read. */
  setWindowOffset(hours: string): void;
  /** Whether the pending-event and draft-preview listeners are registered. */
  listening(): boolean;
  /** Register the pending-event and draft-preview listeners that are not yet. */
  listen(): void;
  /** Paint one subcolumn's spans into its layout. */
  renderSpans(
    $layout: HTMLElement,
    rowId: RowLocalId,
    subcolumn: CalendarSubcolumn,
    occupancy: "shared" | "sole",
    dr: DateRegion,
    column: ColumnIndex.Key.Resolved.WithSubindex,
    windowed: DateFragment.Windowed,
    createHandler: Option<SubcolumnCreateHandler>,
  ): void;
  /** Paint the column's rough boxes (`calendar-rough-layer`); the reveal's presentation end clears them. */
  paintRough(boxes: readonly RoughBox[]): void;
  /** Whether the strip shows nothing now (its load hid it, opacity 0): only then may rough boxes paint. */
  contentHidden(): boolean;
}

/** A strip's bind state. Only this module assigns its fields; the strip reads them. */
export class StripBindState {
  /** Bumped by every bind, load and retire: an older bind's completion checks it. */
  generation = 0;
  /** The props the last bind set. */
  props: Option<CalendarDaysStrip.Props> = null;
  readonly completedRead = new CompletedCalendarRead();
  /** The bind's provider read jobs while a bind step runs (see `withReads`). */
  readJobs: Option<Promise<void>[]> = null;
  /** Synchronous layers' read failures for the bind in progress. */
  readFailures: Option<string[]> = null;
  /** Per-bind provider query cache — see SpanRenderContext.queryCache. Set
   *  per bind; a queued bind's units run across frames, so each unit starts
   *  a fresh cache when the read revision moved since the bind began (N3-6);
   *  null between binds so no stale walk survives a data change. */
  queryCache: Option<EventsCacheSlots> = null;
  /** The observers' bracket of the queued bind in flight; closed when it is superseded (N3-6). */
  queuedSettle: Option<() => void> = null;
  /** The bind's whole UTC range (every day fragment): one SDK span read per row per bind (F2). */
  range: Option<DateTime.Range<Utc>> = null;
  /**
   * The certified read this bind paints under (`nextPaintKey`): the bind's
   * ticket, or the previous bind's ticket object when the read is the same.
   * A layer painted under it for the same fragment already shows what its
   * read would paint, so `StripSpanPaint.render` skips it. Null with no
   * certified read; cleared wherever the completed read is.
   */
  paintKey: Option<CalendarReadTicket> = null;
  /**
   * The strip's column went back to the scroll pool (`parkStrip`) and has
   * not been bound since: it keeps its content and read for a reattach at
   * the same date, but a bind that completes meanwhile shows nothing on
   * screen, so it reports nothing shown.
   */
  parked = false;
}

/**
 * The strip's column went back to the scroll pool; the next bind ends it. Its
 * queued units are dropped (a parked column spends no frame budget, and its
 * queue debt does not hold a settle) and an unfinished bind is fenced; a
 * completed read, the props and the paint key stay for a reattach.
 *
 * With `calendar-idle-complete` the parked strip also registers its idle
 * bind (`parkForIdle`): when the timeline is still, it reads and paints its
 * date off screen, so a fling back finds a completed read to reuse.
 */
export function parkStrip(host: StripBindHost, state: StripBindState, work: Option<CalendarSheetColumn.StripWork>): void {
  state.parked = true;
  cancelQueued(host, state, work);
  state.generation++;
  parkForIdle(host, state, work);
}

/**
 * Register the parked strip's idle bind with the work queue, when idle
 * completion is on and the strip has props: the idle bind (`bindIdle`)
 * reuses a read that is still current and otherwise builds the date again.
 */
function parkForIdle(host: StripBindHost, state: StripBindState, work: Option<CalendarSheetColumn.StripWork>): void {
  const props = state.props;
  if (props == null || work?.park == null || work.enqueueIdle == null || !idleCompleteEnabled()) return;
  const days = dayRangeOf(props);
  work.park(host.key, { first: days.start, last: days.end - 1 }, () => bindIdle(host, state));
}

/**
 * A parked strip's bind in idle time (`StripWorkQueue` idle lane): nothing
 * when the strip is no longer parked, has no props, or its completed read is
 * still reusable; else a fresh bind of its props whose units queue idle. It
 * reports nothing shown (parked); its completed read is what a reattach at the
 * same date reuses.
 */
function bindIdle(host: StripBindHost, state: StripBindState): void {
  const props = state.props;
  if (!state.parked || props == null || !host.connected()) return;
  const inputs = readInputs(host, props);
  const clock = props.readRevision;
  if (clock && inputs && reusable(state, clock, inputs, props)) return;
  runBind(host, state, props, ++state.generation, true);
}

/** Whether the strip's completed read may stand for a bind of `props` (dated with `calendar-dated-read-reuse`). */
function reusable(
  state: StripBindState,
  clock: CalendarReadRevision,
  inputs: readonly CalendarReadValue[],
  props: CalendarDaysStrip.Props,
): boolean {
  return CalendarReadRevision.datedReuse()
    ? state.completedRead.canReuseDays(clock, inputs, dayRangeOf(props))
    : state.completedRead.canReuse(clock, inputs);
}

/**
 * The paint key after a bind that began `ticket`: `previous` when `ticket`
 * certifies the same read (same clock and revision, inputs identical by
 * `Object.is`, as `CompletedCalendarRead.canReuse` compares them), else
 * `ticket` (null: nothing certified, nothing may skip).
 */
export function nextPaintKey(
  previous: Option<CalendarReadTicket>,
  ticket: Option<CalendarReadTicket>,
): Option<CalendarReadTicket> {
  if (previous == null || ticket == null) return ticket;
  const same = previous.clock === ticket.clock && previous.revision === ticket.revision &&
    previous.inputs.length === ticket.inputs.length &&
    previous.inputs.every((value, index) => Object.is(value, ticket.inputs[index]));
  return same ? previous : ticket;
}

/** Forget the strip's completed read and its paint key: the next bind repaints every layer. */
export function clearStripRead(state: StripBindState): void {
  state.completedRead.clear();
  state.paintKey = null;
}

/** The props the last bind set (a read before it is a caller bug). */
export function boundProps(state: StripBindState): CalendarDaysStrip.Props {
  return nonnull(state.props, "CalendarDaysStrip: props read before bind");
}

/** A new load supersedes the bind: drop its queued units and reuse, and fence its completion. */
export function supersedeStripBind(
  host: StripBindHost,
  state: StripBindState,
  work: Option<CalendarSheetColumn.StripWork>,
): void {
  cancelQueued(host, state, work);
  clearStripRead(state);
  // A new date can supersede an in-flight provider paint. Its old completion
  // must not reveal the new date's content while the new load is pending.
  state.generation++;
}

/** End the current bind: drop its queued units, caches and reuse, and fence its completion. */
export function retireStripBind(
  host: StripBindHost,
  state: StripBindState,
  work: Option<CalendarSheetColumn.StripWork>,
): void {
  cancelQueued(host, state, work);
  state.queryCache = null;
  state.range = null;
  state.generation++;
  clearStripRead(state);
}

/** A recycled strip holds no read collection. */
export function recycleStripBind(state: StripBindState): void {
  state.readJobs = null;
}

/** One bind of the strip to `props` (`CalendarDaysStrip.bind`). */
export function bindStrip(host: StripBindHost, state: StripBindState, props: CalendarDaysStrip.Props): void {
  host.deps();
  host.signals();

  host.setBinding("events");
  const work = host.deps().stripWork;
  if (state.parked && host.pooled?.() === true && work?.park != null && idleCompleteEnabled()) {
    // A load that resolved after the column parked: keep the props for the
    // idle bind (and a reattach), build nothing in a frame.
    cancelQueued(host, state, work);
    state.generation++;
    state.props = props;
    parkForIdle(host, state, work);
    return;
  }
  state.parked = false;
  host.deps().contentLag?.loaded?.(host.key, daysOf(props));
  const gen = ++state.generation;
  $$.mutate(() => {
    if (gen !== state.generation) return;
    runBind(host, state, props, gen, false);
  });
}

/**
 * The bind's write task: reuse the completed read when it still stands, else
 * begin a read and build the strip (queued, or synchronous without a work
 * queue). `idle`: the parked strip's idle bind, whose units queue on the idle
 * lane and whose reuse reports nothing shown.
 */
function runBind(
  host: StripBindHost,
  state: StripBindState,
  props: CalendarDaysStrip.Props,
  gen: number,
  idle: boolean,
): void {
  const inputs = readInputs(host, props);
  const clock = props.readRevision;
  if (clock && inputs && host.listening() && reusable(state, clock, inputs, props)) {
    state.props = props;
    host.setBinding(null);
    host.endPresentation();
    if (!state.parked) host.deps().contentLag?.shown(host.key, daysOf(props), "reuse");
    return;
  }
  host.deps().contentLag?.hidden(host.key);
  state.completedRead.clear();
  const ticket = clock && inputs ? state.completedRead.begin(clock, inputs) : null;
  state.paintKey = nextPaintKey(state.paintKey, ticket);
  const jobs: Promise<void>[] = [];
  const failures: string[] = [];
  // paintSpans queues each group's positioning write. Reveal only in the
  // end-write phase after all those writes, never before the provider
  // reads have delivered their content. A failed layer still reveals the
  // layers that painted (see calendar-days-strip-read.ts).
  const settle = () => settleStripReads(jobs, failures, {
    isCurrent: () => gen === state.generation && host.connected(),
    afterWrites: (write) => $$.mutateEnd(write),
    reveal: () => {
      const revealStart = FrameMarks.begin();
      host.setBinding(null);
      host.showContent();
      host.endPresentation();
      if (!state.parked) host.deps().contentLag?.shown(host.key, daysOf(props), "bind");
      FrameMarks.end("strip.reveal", revealStart, 0, 0, props.column.offset);
    },
    complete: () => {
      if (ticket) state.completedRead.complete(ticket);
    },
    fail: (errors, current) => {
      if (ticket) state.completedRead.fail(ticket);
      const detail = { rowId: String(props.rowId), errors };
      if (current) Tracing.err("ui")("calendar provider read failed", detail);
      else Tracing.log("calendar/superseded-read-failed", detail);
    },
  });
  // Every provider participates in presentation readiness, even when its
  // reads cannot be reused across invalidations.
  const work = host.deps().stripWork;
  if (work == null) {
    withReads(state, jobs, failures, () => bindSync(host, state, props));
    void settle();
  } else {
    bindQueued(host, state, props, gen, work, jobs, failures, settle, idle);
  }
  host.listen();
}

/** The days `props` covers (days since the epoch), for the content-lag meter. */
function daysOf(props: CalendarDaysStrip.Props): number[] {
  return props.date[0].toRange().iter().map((date) => date.daysSinceEpoch);
}

/** The days `props` covers as a range (days since the epoch, end exclusive), for the dated reuse. */
function dayRangeOf(props: CalendarDaysStrip.Props): { start: number; end: number } {
  const days = daysOf(props);
  return { start: Math.min(...days), end: Math.max(...days) + 1 };
}

/** Collect a provider read job into the bind step in progress (none between binds). */
export function recordReadJob(state: StripBindState, job: Promise<void>): void {
  state.readJobs?.push(job);
}

/** Collect a synchronous layer's read failure into the bind step in progress (none between binds). */
export function recordReadFailure(state: StripBindState, failure: string): void {
  state.readFailures?.push(failure);
}

/** Run `fn` with the bind's read jobs and failures collecting. */
function withReads(state: StripBindState, jobs: Promise<void>[], failures: string[], fn: () => void): void {
  state.readJobs = jobs;
  state.readFailures = failures;
  try {
    fn();
  } finally {
    state.readJobs = null;
    state.readFailures = null;
  }
}

/**
 * The budgeted bind (F1): the strip's structure now, then one unit per day
 * (read -> spans -> paint, one write task) on the shared `StripWorkQueue`,
 * drained against what each frame has left. The last unit settles the bind
 * (reveal-when-complete). A superseded bind's units are dropped; the bind
 * observers see one bind, bracketing all its units.
 */
function bindQueued(
  host: StripBindHost,
  state: StripBindState,
  props: CalendarDaysStrip.Props,
  gen: number,
  work: CalendarSheetColumn.StripWork,
  jobs: Promise<void>[],
  failures: string[],
  settle: () => Promise<void>,
  idle: boolean,
): void {
  cancelQueued(host, state, work);
  const settleObservers = host.notifyBind(props, gen);
  // A bind superseded before its last unit runs never reaches `finish`;
  // its observers' bracket is closed by `cancelQueued` (N3-6).
  state.queuedSettle = settleObservers;
  const structureStart = FrameMarks.begin();
  beginBind(host, state, props);
  const columns = host.columns();
  FrameMarks.end("strip.bind-sync", structureStart, columns.length, props.subcolumns.length, props.column.offset);
  const finish = () => {
    state.queryCache = null;
    state.range = null;
    state.queuedSettle = null;
    settleObservers?.();
    void settle();
  };
  if (columns.length === 0) return finish();
  const current = () => gen === state.generation && host.connected();
  // Units span frames, so the bind's query cache (and the span read keyed
  // on it) can outlive a data change that has not rebound the strip yet:
  // a unit that finds the read revision moved starts a fresh cache (N3-6).
  let revision = props.readRevision?.value ?? null;
  const freshRead = () => {
    const now = props.readRevision?.value ?? null;
    if (now !== revision) {
      revision = now;
      state.queryCache = { slots: new Map() };
    }
  };
  // A day paints in chunks (one per subcolumn band), each its own unit on the
  // frame budget; the bind finishes after its last chunk (`stripUnits`).
  const units: StripUnit[] = stripUnits(columns.map((column, index) => ({
    day: column.dr.nd.daysSinceEpoch,
    steps: columnSteps(host, state, column).map((step) => () => {
      freshRead();
      const unitStart = FrameMarks.begin();
      withReads(state, jobs, failures, () => {
        try {
          step();
        } catch (error) {
          failures.push(String(error));
        }
      });
      FrameMarks.end("strip.unit", unitStart, index, props.subcolumns.length, props.column.offset);
    }),
  })), current, finish);
  if (idle && work.enqueueIdle != null) {
    work.enqueueIdle(host.key, units);
    return;
  }
  // `calendar-rough-layer` (stage 1b, dom 65): this write, the bind's own at
  // data arrival, draws the strip's rough boxes from the read its day units
  // then paint from, so a fresh column shows them in the first frame after
  // its data lands whether or not a frame admits a strip unit.
  if (roughLayerEnabled() && host.contentHidden()) paintRoughBoxes(host, state, props, columns);
  work.enqueue(host.key, units);
}

/**
 * The bind's rough paint: read each day fragment ahead of its day unit, lay
 * its events into their lanes and paint them as the column's rough boxes; the
 * days count as showing rough content until the reveal.
 */
function paintRoughBoxes(
  host: StripBindHost,
  state: StripBindState,
  props: CalendarDaysStrip.Props,
  columns: readonly DaysStripColumn<SheetNamedChildren>[],
): void {
  const roughStart = FrameMarks.begin();
  const currentTime = host.signals().currentTime.get();
  const boxes = RoughBoxes.forStrip({
    days: columns.map(({ dr, frags }) => ({ tz: dr.tz, frags })),
    subcolumns: props.subcolumns,
    window: props.window,
    partialWindow: props.partialWindow,
    context: (windowed, tz) => ({ windowed, tz, currentTime, queryCache: state.queryCache, bindRange: state.range }),
  });
  if (boxes != null) {
    host.paintRough(boxes);
    host.deps().contentLag?.rough?.(host.key, daysOf(props));
  }
  FrameMarks.end("strip.rough", roughStart, boxes?.length ?? -1, columns.length, props.column.offset);
}

function readInputs(host: StripBindHost, props: CalendarDaysStrip.Props): Option<CalendarReadValue[]> {
  if (!props.readRevision || !props.subcolumns.length || host.dragGhost()) return null;
  const values: CalendarReadValue[] = [
    host.deps(), host.deps().tooltip, host.deps().theme.backgroundColor.backgroundColor(null),
    host.signals(), props.row, props.rowId, props.date[0].toString(),
    props.date[1].fullname, props.column.toString(), props.column.offset, props.window.start.ms,
    props.window.end.ms, props.partialWindow.start?.toString(),
    props.partialWindow.end?.toString(), props.startOfWeek, ...props.readEnvironment(),
  ];
  for (const column of props.subcolumns) {
    if (!column.readIdentity) return null;
    const identity = column.readIdentity();
    values.push(column.rowId, column.providerId, JSON.stringify(column.width),
      column.stagger, column.createHandler != null, identity.length, ...identity);
  }
  return values;
}

/** Drop this strip's queued units and close the superseded bind's observer bracket. */
function cancelQueued(
  host: StripBindHost,
  state: StripBindState,
  work: Option<CalendarSheetColumn.StripWork>,
): void {
  work?.cancel(host.key);
  const settle = state.queuedSettle;
  state.queuedSettle = null;
  settle?.();
}

/**
 * The synchronous bind a same-revision reuse skips: the one step the
 * harness's `onBind` observers see (before it runs; what they return runs
 * after it). With no observer attached it is the bind itself.
 */
function bindSync(host: StripBindHost, state: StripBindState, props: CalendarDaysStrip.Props) {
  const settle = host.notifyBind(props, state.generation);
  if (settle == null) return runBindSync(host, state, props);
  try {
    runBindSync(host, state, props);
  } finally {
    settle?.();
  }
}

function runBindSync(host: StripBindHost, state: StripBindState, props: CalendarDaysStrip.Props) {
  const bindStart = FrameMarks.begin();
  try {
    beginBind(host, state, props);
    for (const column of host.columns()) bindColumn(host, state, column);
  } finally {
    state.queryCache = null;
    state.range = null;
    FrameMarks.end("strip.bind-sync", bindStart, host.cachedDateCount(), props.subcolumns.length, props.column.offset);
  }
}

/**
 * A bind's structure: its props, a fresh bind cache, the window offset, one
 * day element per date (styled), and the current-time listener. The days'
 * content is `bindColumn`'s, per day.
 */
function beginBind(host: StripBindHost, state: StripBindState, props: CalendarDaysStrip.Props): void {
  state.props = props;
  host.resetColumns();
  state.queryCache = { slots: new Map() };
  host.setWindowOffset(String(props.window.start.ms * Time.MS_PER_DAY_INV),
  );
  host.visitColumns(() => {});
  state.range = stripRangeOf(host.columns());
  host.signals().currentTime.addListener(
    host.key,
    (time) => {
      for (const { $day, frags } of host.columns()) {
        for (const frag of frags) {
          if (!frag.frag) continue;
          bindCurrentTime(
            $day.$before,
            frag.frag,
            time,
            props.window,
            props.column,
          );
        }
      }
    },
    LiveData.TriggerImmediately,
  );
}

function bindColumn(host: StripBindHost, state: StripBindState, column: DaysStripColumn<SheetNamedChildren>) {
  for (const step of columnSteps(host, state, column)) step();
}

/**
 * A day's bind as ordered steps: per fragment, its head (fragment bind,
 * gestures) with the first subcolumn band, one step per further band, and
 * its tail (layout order, occlusion) with the last band. Run in order they
 * paint exactly what one pass did.
 */
function columnSteps(host: StripBindHost, state: StripBindState, {
  $day,
  frags,
  dr,
  subindex,
}: DaysStripColumn<SheetNamedChildren>): (() => void)[] {
  const steps: (() => void)[] = [];
  const { rowId, window, subcolumns, partialWindow } = boundProps(state);
  const occupancy = subcolumns.length > 1 ? "shared" : "sole";
  let fragmentsOverlap = false;
  const beforeFrag = frags[0]?.frag ?? null;
  const afterFrag = frags[1]?.frag ?? null;
  if (beforeFrag != null && afterFrag != null) {
    fragmentsOverlap = afterFrag.start.time.isBefore(
      beforeFrag.end.time,
    );
  }

  for (const { frag, $frag, otherId } of frags) {
    // Shared by the fragment's steps: set by its head, read by its bands and tail.
    let live = false;
    let windowed: Option<DateFragment.Windowed> = null;
    const $layouts: HTMLElement[] = [];
    let rendered: Set<ReturnType<typeof $frag.renderedLayouts>[number]> = new Set();
    const fragHead = () => {
      windowed = frag
        ? new DateFragment.Windowed(frag, partialWindow, window)
        : null;
      const intersects = $day.bindFragment($frag, windowed);
      if (!intersects || !windowed) return;

      $day.bindFragmentsOverlap(fragmentsOverlap);
      if (!frag) return;
      live = true;

      $frag.setOnTooltip(() => $day.setAttribute("revealed", otherId));
      const columnCreateHandler =
        subcolumns.find((s) => s.createHandler != null)?.createHandler ?? null;
      // All creatable handlers, deduped by the discovery-stamped
      // provider id (first per id, subcolumn order). Purely
      // mechanical — N rows resolved by the same plugin collapse to
      // one entry; this layer never interprets the ids. App-layer
      // gestures pick the entry matching their create flow.
      const createEntries: GestureInfo.CreateEntry[] = [];
      const seenProviderIds = new Set<string>();
      for (const s of subcolumns) {
        if (!s.createHandler) continue;
        const key = s.providerId ?? null;
        if (key != null) {
          if (seenProviderIds.has(key)) continue;
          seenProviderIds.add(key);
        }
        createEntries.push({ key, handler: s.createHandler });
      }
      $frag.setCallbacks(
        host.deps().gestureManager.asColumnDelegate(
          new GestureInfo(
            rowId,
            null,
            boundProps(state).column.withSubindex(subindex),
            windowed,
            columnCreateHandler,
            createEntries,
          ),
        ),
      );

      rendered = new Set($frag.renderedLayouts());
    };
    const band = (subcolumn: CalendarSubcolumn) => () => {
      if (!live || windowed == null) return;
      rendered.delete(subcolumn.rowId);
      const subCreateHandler = subcolumn.createHandler ?? null;

      const $layout = $frag.getLayoutUntyped(
        subcolumn.rowId,
        subcolumn.makeLayout,
      );
      const column = boundProps(state).column.withSubindex(subindex);
      const renderContext: CalendarSubcolumn.LayoutBindContext = {
        windowed: windowed,
        tz: dr.tz,
        currentTime: host.signals().currentTime.get(),
        queryCache: state.queryCache,
        bindRange: state.range,
        bandRowId: rowId,
        column,
      };
      subcolumn.bindLayout?.($layout, renderContext);
      host.renderSpans(
        $layout,
        rowId,
        subcolumn,
        occupancy,
        dr,
        column,
        windowed,
        subCreateHandler,
      );
      $layouts.push($layout);
    };
    const fragTail = () => {
      if (!live || windowed == null) return;
      $frag.sortLayouts($layouts);

      for (const remaining of rendered) {
        $frag.removeLayout(remaining);
      }

      // Render more indicators for occluded events
      renderOccludedIndicators(
        $frag,
        windowed,
        subcolumns,
        boundProps(state).column.withSubindex(subindex),
        occlusionContext(host, state),
      );
    };
    const bands = subcolumns.map(band);
    steps.push(...(bands.length === 0 ? [() => (fragHead(), fragTail())] : bands.map((step, k) => () => {
      if (k === 0) fragHead();
      step();
      if (k === bands.length - 1) fragTail();
    })));
  }
  return steps;
}

/** What the occlusion indicators read: this bind's query cache, and the strip's current props and deps (read when clicked). */
function occlusionContext(host: StripBindHost, state: StripBindState): OcclusionContext {
  return { queryCache: state.queryCache, props: () => boundProps(state), deps: () => host.deps() };
}

/** The UTC range covering every day fragment of a strip's columns (null with none). */
function stripRangeOf(columns: readonly DaysStripColumn<SheetNamedChildren>[]): Option<DateTime.Range<Utc>> {
  let start: Option<DateTime<Utc>> = null;
  let end: Option<DateTime<Utc>> = null;
  for (const { frags } of columns) {
    for (const { frag } of frags) {
      if (frag == null) continue;
      const fragStart = frag.start.toUtc(), fragEnd = frag.end.toUtc();
      if (start == null || fragStart.mse < start.mse) start = fragStart;
      if (end == null || fragEnd.mse > end.mse) end = fragEnd;
    }
  }
  return start == null || end == null ? null : new DateTime.Range(start, end);
}
